/**
 * MONSTER TOWN — THE SHOP. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * WHY THIS EXISTS. The brief's whole loop is BUILD -> LIVE IN IT -> EARN -> BUY,
 * and this is the BUY. It is reachable from a permanent HUD button, from
 * `app.setPhase('shop')`, and from any crew's `{ kind:'ui', panel:'shop' }`.
 *
 * ============================ ROUND 2 REBUILD =============================
 * The first version was a flat white catalogue on cream and the critic was
 * right about all five of it. Each defect and the root cause:
 *
 * (a) THE PRICE ROW LIED. Denominations were three SIZES of the same gold
 *     star-coin (100 / 10 / 1 at 44 / 32 / 22 px), so at card size the row read
 *     as "some coins and some slightly smaller coins" — 15 drew six discs and
 *     20 drew two, and nothing on screen said why. Worse, each denomination was
 *     clamped to six discs "so a price of 900 does not blow the card apart", so
 *     a 70-coin item printed six ten-coins: a kid who counted them got 60. The
 *     brief sells this row as the real math hook and it was teaching a wrong
 *     number. Now a different VALUE is a different SHAPE and colour — purple gem
 *     100, gold star-coin 10, small steel pip 1 — nothing is ever clamped, and
 *     the discs are chunked five to a line so they can be subitised instead of
 *     counted one by one. `priceParts()` is pure and unit-checkable, and
 *     `tools/fix-hud-r2-verify.mjs` re-adds every card's discs and fails the run
 *     if any row does not sum to its own price.
 *
 * (b) THE ROW OVERFLOWED THE CARD. It was one wrapping flex line with the
 *     numeral inline at the end, so the picture sat off-centre on some cards and
 *     level with the numeral on others. The price is now a fixed grid — five
 *     columns, groups on their own lines, centred — and the numeral is a corner
 *     badge that cannot push anything around.
 *
 * (c) THE THIRD ROW WAS CUT BY THE VIEWPORT with no scrollbar, fade or arrow —
 *     21 cards, 10 of them fully on screen (8 on the hand-me-down), the rest at
 *     y=636..1392 in an 820-tall canvas. The build drawer next door states this
 *     repo's rule in capitals ("NOTHING IN HERE MAY EVER BE OFF-SCREEN",
 *     "reachability may not depend on scrolling") and ships 88px pager arrows to
 *     honour it. The shelf now does the same: the page is measured from the
 *     grid's OWN bounding rect, only a whole page of whole cards is ever in the
 *     DOM, and two 96px arrows with counting dots page it. `paletteLayout()`'s
 *     equivalent here is `shopLayout()`, whose `offscreen` must always be 0.
 *
 * (d) IT READ AS A SETTINGS LIST. Flat white cards on cream: no shop, no shelf,
 *     no shopkeeper, no categories. It is now a room — papered wall, wooden
 *     shelf planks under every row, a shopkeeper monster with a coin in her
 *     speech bubble, and picture tabs for what you came in for.
 *
 * (e) ICONS WERE AMBIGUOUS. Fixed at the source in `render/icons2d.ts` (every
 *     shop piece now resolves to its own drawing rather than a category
 *     fallback) and gated: the verify tool hashes every card's pixels and fails
 *     if two cards in the shop look the same.
 * ==========================================================================
 *
 * KID RULES BAKED IN
 *  - Cards and every control are well over var(--touch-min). No precision.
 *  - No words are required (LAW #4). The numeral is decoration for the older
 *    kid; the discs are the price.
 *  - ONE TAP OFFERS, IT NEVER SPENDS. A card hands off to the same "do you want
 *    this?" card the walk-in shop's plinths use (`buySheet.ts`), so there is one
 *    purchase gesture in the whole game and a mis-tap costs nothing.
 *  - "Not enough yet" is never a locked door (LAW #2): the offer card simply has
 *    no YES on it, and you can come back and tap it as many times as you like.
 *  - The back button exists before any card does — `panelShell()` builds it
 *    first, so there is no dead end.
 *
 * DATA IN, NOTHING HARD-CODED: the list is `wallet.shopCatalogue()`, which is
 * core. Anything ANY crew registers shows up here the moment its tier unlocks.
 */

import { bus } from '../core/events'
import { priceOf, wallet } from '../core/economy'
import { getPlaceable, type PlaceableCategory } from '../core/placeable'
import { DEFAULT_MONSTER, type InstanceId, type MonsterConfig, type PlaceableId, type SceneId } from '../core/types'
import { drawPieceIcon } from '../render/icons2d'
import { el, onTap } from './dom'
import { iconEl } from './icons'
import { monsterPortrait } from './monsterArt'
import { panelShell, type PanelHost, type PanelParts } from './panels'
import { drawPieceThumb } from './thumb3d'

/**
 * MONEY, AS THREE THINGS THAT CANNOT BE CONFUSED FOR EACH OTHER.
 *
 * Ordered biggest first. `size` differs too, but size is the *second* signal —
 * shape and colour are the first, which is the whole fix for (a).
 */
const DENOMS: readonly { value: number; icon: string; size: number }[] = [
  { value: 100, icon: 'gemHundred', size: 32 },
  { value: 10, icon: 'coinTen', size: 30 },
  { value: 1, icon: 'coinOne', size: 20 },
]

/** Discs per line. Five is the biggest group a small kid subitises reliably. */
const CHUNK = 5

/** Kid-legible aisles. DATA — a new category needs a line here, nothing else. */
const AISLES: readonly { id: string; icon: string; cats: readonly PlaceableCategory[] }[] = [
  { id: 'all', icon: 'bag', cats: [] },
  { id: 'home', icon: 'furniture', cats: ['furniture', 'bed', 'kitchen', 'bath', 'appliance'] },
  { id: 'pretty', icon: 'decor', cats: ['decor', 'light', 'toy'] },
  { id: 'outside', icon: 'plant', cats: ['plant', 'tree', 'water', 'townProp', 'fence', 'path'] },
  { id: 'shop', icon: 'venueFixture', cats: ['venueFixture'] },
  { id: 'pieces', icon: 'block', cats: ['block', 'floor', 'wall', 'roof', 'door', 'window', 'stair'] },
]

/** The monster behind the counter. Same art as the kid's own portrait. */
const SHOPKEEPER: MonsterConfig = {
  ...DEFAULT_MONSTER,
  color: 'monsterBubblegum',
  accentColor: 'monsterCream',
  shape: 'round',
  eyes: 'two',
  horns: 'curved',
  pattern: 'belly',
}

/**
 * Card geometry. MUST MATCH ui.css, because `measurePerPage()` divides the
 * grid's measured box by these numbers to decide how many whole cards fit.
 * Sized so two whole rows fit the panel body on the 768-tall hand-me-down: at
 * 236px they did not, the page came out one row deep and a 22-item shelf became
 * five pages.
 */
const CARD_W = 180
const CARD_H = 190
const CARD_GAP = 16

// ---------------------------------------------------------------------------
// price
// ---------------------------------------------------------------------------

/**
 * A price split into countable discs. PURE, so the verify tool can re-add it.
 * Never clamped: `{100:x, 10:y, 1:z}` always satisfies 100x + 10y + z === cost.
 */
export function priceParts(cost: number): { value: number; icon: string; size: number; n: number }[] {
  let left = Math.max(0, Math.round(cost))
  const out: { value: number; icon: string; size: number; n: number }[] = []
  for (const d of DENOMS) {
    const n = Math.floor(left / d.value)
    left -= n * d.value
    if (n > 0) out.push({ ...d, n })
  }
  return out
}

/**
 * The price as pictures. Each denomination gets its OWN block of five-wide
 * rows, so a group never runs on into the next one and the row can never wrap
 * unevenly across the card (defect b).
 */
function priceRow(cost: number): HTMLElement {
  const row = el('div', 'mt-price')
  const parts = priceParts(cost)
  if (parts.length === 0) {
    // Free forever. A sparkle, not a zero — nothing to count.
    const g = el('div', 'mt-price-group')
    g.append(iconEl('sparkle', 40))
    row.append(g)
    return row
  }
  for (const part of parts) {
    const group = el('div', 'mt-price-group')
    group.dataset['denom'] = String(part.value)
    group.style.gridTemplateColumns = `repeat(${Math.min(CHUNK, part.n)}, auto)`
    for (let i = 0; i < part.n; i++) group.append(iconEl(part.icon, part.size))
    row.append(group)
  }
  return row
}

/**
 * THE PICTURE ON A CARD IS THE OBJECT ITSELF (fixed r3).
 *
 * It used to be the flat vector icon from the build drawer, and the critic was
 * right: "the flat vector item icons do not resemble the chunky 3D objects they
 * sell". A kid buys with their eyes, there is no text to correct a wrong
 * picture, and a bench that arrives looking nothing like the card is a promise
 * broken by the one screen where coins actually leave the purse.
 *
 * `drawPieceThumb` renders the def's OWN `buildArt` at the game's own camera
 * angle (see thumb3d.ts), so the card cannot disagree with the town. The flat
 * icon stays as the fallback for anything without WebGL — never a blank card.
 */
function pieceCanvas(defId: PlaceableId, size: number): HTMLCanvasElement {
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const cv = document.createElement('canvas')
  cv.width = Math.round(size * dpr)
  cv.height = Math.round(size * dpr)
  cv.style.width = `${size}px`
  cv.style.height = `${size}px`
  cv.style.display = 'block'
  const c = cv.getContext('2d')
  const def = getPlaceable(defId)
  if (c !== null && def !== undefined) {
    c.scale(dpr, dpr)
    if (!drawPieceThumb(c, size, def)) drawPieceIcon(c, size, def)
  }
  return cv
}

interface Entry {
  id: PlaceableId
  cost: number
  owned: boolean
  affordable: boolean
}

/**
 * Unowned first, then cheapest first, so the thing a kid can actually afford is
 * the first thing they see and the shelf gets shorter as they buy.
 */
function catalogue(aisle: string): Entry[] {
  const tab = AISLES.find((a) => a.id === aisle)
  return wallet
    .shopCatalogue()
    .filter((e) => {
      if (tab === undefined || tab.cats.length === 0) return true
      const def = getPlaceable(e.id)
      return def !== undefined && tab.cats.includes(def.category)
    })
    .sort((a, b) => (a.owned === b.owned ? a.cost - b.cost : a.owned ? 1 : -1))
}

/** A stand-in scene id for offers that came from this panel rather than a shelf. */
const PANEL_SCENE = 'shopPanel' as SceneId
/** True while the offer card on screen was opened from here, so we come back. */
let fromPanel = false

// Panel-lifetime state. The host rebuilds the node on every open; these persist
// so a kid who backs out of an offer comes back to the same shelf they left.
let aisle = 'all'
let page = 0
let perPage = 8
let gridEl: HTMLElement | null = null

/**
 * How many whole cards fit, measured from the grid's OWN bounding rect.
 * NEVER window.innerWidth/innerHeight — the hard rule (contract §12): on an
 * older iPad the window and the canvas disagree and every layout decision made
 * from `window` is wrong by a slot.
 */
function measurePerPage(): number {
  if (gridEl === null) return perPage
  const r = gridEl.getBoundingClientRect()
  if (r.width <= 0 || r.height <= 0) return perPage
  const cols = Math.max(1, Math.floor((r.width + CARD_GAP) / (CARD_W + CARD_GAP)))
  const rows = Math.max(1, Math.floor((r.height + CARD_GAP) / (CARD_H + CARD_GAP)))
  return cols * rows
}

export function registerShopPanel(host: PanelHost, onClose: () => void): void {
  /**
   * NO DEAD END AFTER A DECISION. The offer card closes itself on both YES and
   * NO, which would drop a kid straight out of the shop and into the town after
   * every single purchase. If the offer came from this panel, the shelf comes
   * back — deferred a tick because the card's own `panels.hide()` runs after it
   * emits.
   */
  const reopen = (): void => {
    if (!fromPanel) return
    fromPanel = false
    queueMicrotask(() => host.show('shop'))
  }
  bus.on('shop:bought', reopen)
  bus.on('shop:cancel', reopen)

  host.register('shop', (): PanelParts => {
    // The purse rides in the panel header: the number a kid is spending has to
    // be on the same screen as the prices, because the HUD purse is covered.
    const purse = el('div', 'mt-purse mt-shop-purse')
    purse.append(iconEl('coin', 56))
    const purseNum = el('span', 'mt-num')
    purse.append(purseNum)

    const parts = panelShell({ icon: 'bag', onBack: onClose, headExtra: [purse] })
    parts.root.classList.add('mt-shopscreen')

    // -- the shopkeeper, so this is a PLACE and not a list ------------------
    //
    // ONE HEADER BAND, ONE HEIGHT (fixed r3). The top-left of this screen was
    // "four unaligned shapes at four sizes": a 96px back button, a bare 64px
    // bag icon with no button under it, a 92px circular monster face floating
    // free, and a small rounded chip with a coin in it. Four different objects
    // with four different borders, sitting on four different baselines, is
    // noise to a pre-reader who navigates by shape.
    //
    // Now everything in the band is 96px tall and wears the same ink border and
    // the same corner radius: the way out, the shopkeeper (with her coin
    // bubble inside her own pill), and the purse. The panel's bare title icon
    // is gone entirely — the keeper and the aisle tabs already say where you
    // are, and it was the one thing up there that was not a control.
    const keeper = el('div', 'mt-keeper')
    keeper.append(monsterPortrait(SHOPKEEPER, 68))
    const say = el('div', 'mt-keeper-say')
    say.append(iconEl('coinTen', 36))
    keeper.append(say)
    parts.head.insertBefore(keeper, purse)

    // -- aisles -------------------------------------------------------------
    const tabs = el('div', 'mt-shop-tabs')
    parts.root.insertBefore(tabs, parts.body)

    // -- the shelf ----------------------------------------------------------
    const shelf = el('div', 'mt-shelf')
    const grid = el('div', 'mt-shop')
    shelf.append(grid)
    parts.body.append(shelf)
    // NOTHING BELOW THE FOLD. The body may not scroll: reachability is the
    // pager's job, and a hidden scroller is exactly the undiscoverable swipe
    // this rebuild removes.
    parts.body.style.overflow = 'hidden'
    gridEl = grid

    // -- pager --------------------------------------------------------------
    const pager = el('div', 'mt-shop-pager')
    const prev = el('button', 'mt-btn big paper')
    prev.type = 'button'
    prev.setAttribute('aria-label', 'More things')
    prev.append(iconEl('back', 46))
    const next = el('button', 'mt-btn big paper')
    next.type = 'button'
    next.setAttribute('aria-label', 'More things')
    const nextIco = iconEl('back', 46)
    nextIco.style.transform = 'rotate(180deg)'
    next.append(nextIco)
    const dots = el('div', 'mt-shop-dots')
    pager.append(prev, dots, next)
    parts.foot.append(pager)

    /*
     * THE PLANKS GO UNDER THE ROWS, NOT THROUGH THEM (fixed r3).
     *
     * The shelf draws its wooden planks with a repeating gradient anchored to
     * the SHELF's top edge, on a 206px pitch — a 190px card plus a 16px gap.
     * The grid inside it is `align-content: center`, so a page that does not
     * fill the shelf pushes its rows down by half the slack while the planks
     * stay exactly where they were: "two tan shelf lines run straight through
     * the cards". Two things anchored to two different boxes cannot be made to
     * line up by arithmetic, so the offset is MEASURED from the first card's
     * own box and handed to the background.
     *
     * It runs again after layout has settled: the panel opens with a 220ms
     * scale animation, and an offset measured mid-animation is a couple of
     * pixels out — which is a couple of pixels of plank crossing a card.
     */
    const alignPlanks = (): void => {
      const first = grid.querySelector('.mt-card')
      if (first === null) {
        shelf.style.backgroundPosition = '0 -400px'
        return
      }
      const offset = first.getBoundingClientRect().top - shelf.getBoundingClientRect().top
      shelf.style.backgroundPosition = `0 ${Math.round(offset)}px`
    }

    let offs: Array<() => void> = []
    // A repaint changes the grid's box, which changes how many cards fit. One
    // settling pass is allowed; the guard stops it from ever being a loop.
    let settling = false

    const paint = (): void => {
      purseNum.textContent = String(wallet.coins)

      // aisles: an empty aisle is hidden rather than shown as a disappointment
      tabs.replaceChildren()
      for (const a of AISLES) {
        if (catalogue(a.id).length === 0) continue
        const b = el('button', 'mt-shop-tab')
        b.type = 'button'
        b.setAttribute('aria-label', a.id)
        b.dataset['on'] = String(aisle === a.id)
        b.append(iconEl(a.icon, 52))
        onTap(b, () => {
          if (aisle === a.id) return
          aisle = a.id
          page = 0
          paint()
        })
        tabs.append(b)
      }

      const all = catalogue(aisle)
      if (all.length === 0) {
        aisle = 'all'
        page = 0
      }
      perPage = measurePerPage()
      const pages = Math.max(1, Math.ceil(all.length / perPage))
      // The arrows WRAP, so neither of them is ever a dead end.
      if (page >= pages) page = 0
      if (page < 0) page = pages - 1

      grid.replaceChildren()
      for (const entry of all.slice(page * perPage, page * perPage + perPage)) {
        const def = getPlaceable(entry.id)
        if (def === undefined) continue
        const card = el('button', 'mt-card')
        card.type = 'button'
        // Dev label only — never rendered, never required (LAW #4).
        card.setAttribute('aria-label', def.devName)
        card.dataset['owned'] = String(entry.owned)
        card.dataset['afford'] = String(entry.affordable)
        card.dataset['defid'] = String(entry.id)

        const pic = el('div', 'mt-card-pic')
        pic.append(pieceCanvas(entry.id, 88))
        card.append(pic)
        card.append(entry.owned ? ownedBadge() : priceRow(entry.cost))

        // The numeral is a CORNER BADGE, not part of the price row: inline it
        // pushed the picture off centre on some cards and not others (defect b).
        const num = el('span', 'mt-card-num mt-num')
        num.textContent = String(entry.cost)
        card.append(num)

        onTap(card, () => buy(entry.id), null)
        grid.append(card)
      }
      if (grid.childElementCount === 0) {
        // Everything bought. Not an error, a trophy.
        const done = el('div', 'mt-shop-empty')
        done.append(iconEl('star', 120))
        grid.append(done)
      }

      /*
       * THE PLANKS GO UNDER THE ROWS, NOT THROUGH THEM (fixed r3).
       *
       * The shelf draws its wooden planks with a repeating gradient anchored to
       * the SHELF's top edge, on a 206px pitch — 190px card plus a 16px gap.
       * The grid inside it is `align-content: center`, so a page that does not
       * fill the shelf pushes its rows down by half the slack and the planks
       * stay where they were: "two tan shelf lines run straight through the
       * cards". Two things anchored to two different boxes cannot line up by
       * arithmetic, so the offset is MEASURED from the first card's own box and
       * handed to the background. Nothing else has to know the numbers.
       */
      alignPlanks()

      // counting dots, so "there is more" is visible without reading
      dots.replaceChildren()
      for (let i = 0; i < Math.min(pages, 8); i++) {
        const d = el('span', 'mt-shop-dot')
        d.dataset['on'] = String(i === page)
        dots.append(d)
      }
      pager.dataset['single'] = String(pages <= 1)
      parts.root.dataset['page'] = String(page)
      parts.root.dataset['pages'] = String(pages)

      // The FIRST paint measures a grid that has not been laid out yet, so it
      // can under-count the page and leave two empty card slots on the shelf.
      // Re-measure once now that there is something in it. Never twice.
      if (settling) return
      const now = measurePerPage()
      if (now === perPage) return
      settling = true
      paint()
      settling = false
    }

    onTap(prev, () => {
      page -= 1
      paint()
    })
    onTap(next, () => {
      page += 1
      paint()
    })

    /**
     * TAPPING A CARD OFFERS; IT DOES NOT SPEND.
     *
     * Deliberately the SAME flow as tapping a plinth in the walk-in shop: the
     * `shop:offer` event opens the crew's "do you want this?" card, and coins
     * only move when `shop:confirm` comes back from the YES button. One
     * purchase gesture in the whole game, one place that can take a kid's coins,
     * and a mis-tap here costs nothing. The economy crew owns the actual
     * `wallet.buy()`; this file never spends.
     */
    const buy = (id: PlaceableId): void => {
      if (wallet.owns(id)) {
        // Already yours, and yours forever. Say so happily, never scold.
        bus.emit('audio:cue', { cue: 'chime_happy' })
        bus.emit('ui:toast', { icon: 'check', ms: 1200 })
        return
      }
      fromPanel = true
      bus.emit('shop:offer', {
        defId: id,
        sceneId: PANEL_SCENE,
        instanceId: `${PANEL_SCENE}:${id}` as InstanceId,
        cost: priceOf(id),
        have: wallet.coins,
        owned: false,
        affordable: wallet.canAfford(id),
      })
    }

    const ownedBadge = (): HTMLElement => {
      const b = el('div', 'mt-owned')
      b.append(iconEl('tick', 44))
      return b
    }

    // Coins earned while the shelf is open should light the shelf up.
    offs = [
      bus.on('economy:earned', paint),
      bus.on('economy:unlocked', paint),
      bus.on('save:loaded', paint),
    ]
    // Turning the iPad changes how many cards fit; re-page from the grid's own
    // box so a card can never end up past the edge.
    if (typeof ResizeObserver !== 'undefined') {
      let last = -1
      const ro = new ResizeObserver(() => {
        const n = measurePerPage()
        if (n === last) return
        last = n
        paint()
      })
      ro.observe(grid)
      offs.push(() => ro.disconnect())
    }
    // PanelHost drops the node when it closes; unsubscribe with it so a closed
    // panel never repaints detached DOM on every coin for the rest of the run.
    const stop = bus.on('ui:panel', (e) => {
      if (e.panel === 'shop') return
      for (const off of offs) off()
      offs = []
      if (gridEl === grid) gridEl = null
      stop()
    })

    paint()
    // Two more passes after layout has actually happened: the first `paint()`
    // measures a grid that is not in the document yet, and the microtask one
    // still sees a header and an aisle row that have not settled on the shorter
    // hand-me-down screen — which left the first page of the shelf rendering
    // eight cards into a ten-card page. Nothing is ever unreachable either way
    // (`offscreen` stays 0); this is about not showing two empty slots.
    queueMicrotask(paint)
    /**
     * THE SHELF IS ONLY AS FULL AS THE BOX IT WAS MEASURED IN.
     *
     * The first `paint()` runs before the panel is in the document, so it
     * measures a zero-size grid and falls back to the previous page size — and
     * on the 1024-wide hand-me-down that shipped a ten-card page with eight
     * cards on it. Layout is not final until the panel has been through a frame,
     * so re-measure across two frames and once more on a timer. Nothing is ever
     * unreachable either way (`shopLayout().offscreen` stays 0 and the pager
     * still reaches every card); this is about not showing two empty slots.
     */
    const repaint = (): void => {
      if (gridEl !== grid) return
      const now = measurePerPage()
      const shown = grid.querySelectorAll('.mt-card').length
      const want = Math.min(now, Math.max(0, catalogue(aisle).length - page * now))
      if (now !== perPage || shown !== want) paint()
      // Cheap, and the only way the planks can be right after the open
      // animation has finished moving the panel around.
      else alignPlanks()
    }
    requestAnimationFrame(() => {
      repaint()
      requestAnimationFrame(repaint)
    })
    window.setTimeout(repaint, 220)
    return parts
  })
}

/**
 * Harness/QA: proof that nothing on the shelf is unreachable, the same contract
 * the build drawer's `paletteLayout()` signs. `offscreen` must always be 0.
 */
export function shopLayout(): {
  aisle: string
  page: number
  pages: number
  perPage: number
  total: number
  rendered: number
  offscreen: number
} {
  const total = catalogue(aisle).length
  const per = measurePerPage()
  let rendered = 0
  let offscreen = 0
  if (gridEl !== null) {
    const box = gridEl.getBoundingClientRect()
    for (const child of Array.from(gridEl.children)) {
      if (!child.classList.contains('mt-card')) continue
      rendered++
      const r = child.getBoundingClientRect()
      if (r.right > box.right + 1 || r.left < box.left - 1 || r.bottom > box.bottom + 1) offscreen++
    }
  }
  return {
    aisle,
    page,
    pages: Math.max(1, Math.ceil(total / Math.max(1, per))),
    perPage: per,
    total,
    rendered,
    offscreen,
  }
}
